import React, { useEffect, useState, useRef } from "react";
import { getFirestore, doc, setDoc, getDoc, updateDoc, arrayUnion, increment } from "firebase/firestore";
import { getAuth, signInAnonymously } from "firebase/auth";
import debounce from '../../../utility/login_debounce';

const db = getFirestore();
const auth = getAuth();
const DEFAULT_REACTIONS = { likes: 0, hearts: 0, claps: 0 };
const ANONYMOUS_VISITOR_ID_KEY = "blogAnonymousVisitorId";

const normalizeReactions = (incomingReactions = {}) => ({
    likes: incomingReactions.likes || 0,
    hearts: incomingReactions.hearts || 0,
    claps: incomingReactions.claps || 0,
});

const getAnonymousVisitorId = () => {
    if (typeof window === "undefined") return "anonymous-visitor";

    const existingId = window.localStorage.getItem(ANONYMOUS_VISITOR_ID_KEY);
    if (existingId) return existingId;

    const newId = `anonymous-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    window.localStorage.setItem(ANONYMOUS_VISITOR_ID_KEY, newId);
    return newId;
};

const Comments = ({ postId }) => {
    const [reactions, setReactions] = useState(DEFAULT_REACTIONS);
    const [comments, setComments] = useState([]);
    const [commentText, setCommentText] = useState("");
    const [pendingReaction, setPendingReaction] = useState(null);
    let debouncedButton = useRef(debounce(() => { }, 255));

    const getCommentIdentity = async (user) => {
        if (!user || user.isAnonymous) {
            return {
                user: user?.uid || getAnonymousVisitorId(),
                username: "Anonymous",
            };
        }

        const userRef = doc(db, "users", user.uid);
        const userSnap = await getDoc(userRef);
        const profileUsername = userSnap.exists() ? userSnap.data().username : "";

        return {
            user: user.email || user.uid,
            username: user.displayName || profileUsername || user.email || "Anonymous",
        };
    };

    const ensureAnonymousFirebaseUser = async () => {
        if (auth.currentUser) return auth.currentUser;

        try {
            const result = await signInAnonymously(auth);

            await setDoc(doc(db, "users", result.user.uid), {
                uid: result.user.uid,
                username: "Anonymous",
                email: null,
                isAnonymous: true,
                lastLogin: new Date().toISOString(),
            }, { merge: true });

            return result.user;
        } catch (error) {
            console.warn("Anonymous Firebase sign-in failed. Continuing with local anonymous guest.", error);
            return null;
        }
    };

    // Fetch post data from Firestore
    useEffect(() => {
        const fetchPost = async () => {
            const postRef = doc(db, "posts", postId);
            const postSnap = await getDoc(postRef);
            if (postSnap.exists()) {
                const data = postSnap.data();
                setReactions(normalizeReactions(data.reactions));
                setComments(data.comments || []);
            } else {
                setReactions(DEFAULT_REACTIONS);
                setComments([]);
            }
        };
        fetchPost();
    }, [postId]);

    // Handle Reactions
    const handleReaction = async (type) => {
        if (pendingReaction) return;

        const postRef = doc(db, "posts", postId);
        const previousReactions = normalizeReactions(reactions);
        const newReactions = { ...previousReactions, [type]: previousReactions[type] + 1 };

        setPendingReaction(type);
        setReactions(newReactions);

        try {
            await ensureAnonymousFirebaseUser();
            await setDoc(postRef, { reactions: { [type]: increment(1) } }, { merge: true });
        } catch (error) {
            console.error("Reaction failed:", error);
            setReactions(previousReactions);
            alert("Reaction could not be saved. Please try again.");
        } finally {
            setPendingReaction(null);
        }
    };

    const onSubmit = (e) => {
        e.preventDefault();
        debouncedButton.current = debounce(() => { handleCommentSubmit() }, 255, debouncedButton.current());
        if (typeof debouncedButton.current === 'function') {
            debouncedButton.current();
        }
    }

    // Handle Comment Submission
    const handleCommentSubmit = async () => {
        if (!commentText.trim()) {
            return;
        }

        try {
            const user = auth.currentUser || await ensureAnonymousFirebaseUser();
            const identity = await getCommentIdentity(user);

            const newComment = {
                id: Date.now(),
                text: commentText.trim(),
                user: identity.user,
                likes: 0,
                username: identity.username
            };

            const postRef = doc(db, "posts", postId);
            const postSnap = await getDoc(postRef);

            if (!postSnap.exists()) {
                await setDoc(postRef, { comments: [newComment], reactions: DEFAULT_REACTIONS });
            } else {
                await updateDoc(postRef, {
                    comments: arrayUnion(newComment),
                });
            }

            setComments((prev) => [...prev, newComment].sort((a, b) => b.likes - a.likes));
            setCommentText("");
        } catch (error) {
            console.error("Comment failed:", error);
            alert("Comment could not be saved. Please try again.");
        }
    };

    const handleLikeComment = async (commentId) => {
        try {
            const user = auth.currentUser || await ensureAnonymousFirebaseUser();
            const userLikeId = user?.uid || getAnonymousVisitorId();
            const postRef = doc(db, "posts", postId);
            const postSnap = await getDoc(postRef);

            if (postSnap.exists()) {
                let updatedComments = (postSnap.data().comments || []).map((comment) => {
                    if (comment.id === commentId) {
                        let newLikes = comment.likes || 0;
                        let newLikedBy = comment.likedBy ? [...comment.likedBy] : [];

                        if (newLikedBy.includes(userLikeId)) {
                            newLikes -= 1;
                            newLikedBy = newLikedBy.filter(uid => uid !== userLikeId);
                        } else {
                            newLikes += 1;
                            newLikedBy.push(userLikeId);
                        }

                        return {
                            ...comment,
                            likes: newLikes,
                            likedBy: newLikedBy,
                        };
                    }
                    return comment;
                });

                // Sort comments by likes (highest first)
                updatedComments = updatedComments.sort((a, b) => b.likes - a.likes);

                await updateDoc(postRef, { comments: updatedComments });
                setComments(updatedComments);
            }
        } catch (error) {
            console.error("Comment like failed:", error);
            alert("Like could not be saved. Please try again.");
        }
    };


    return (
        <div className="container mt-4">
            {/* Reactions Section */}
            <div className="card shadow-sm p-3">
                <h4 className="text-left">React to this Post</h4>
                <div className="d-flex justify-content-left gap-3">
                    <button className="btn btn-outline-primary" onClick={() => handleReaction("likes")} disabled={Boolean(pendingReaction)}>
                        👍 {reactions.likes}
                    </button>
                    <button className="btn btn-outline-danger" onClick={() => handleReaction("hearts")} disabled={Boolean(pendingReaction)}>
                        &#x2764;&#xFE0F; {reactions.hearts}
                    </button>
                    <button className="btn btn-outline-warning" onClick={() => handleReaction("claps")} disabled={Boolean(pendingReaction)}>
                        👏 {reactions.claps}
                    </button>
                </div>
            </div>

            {/* Comments Section */}
            <div className="card mt-3 shadow-sm p-3">
                <h4 className="text-center">Comments</h4>
                <form className="mb-3" onSubmit={onSubmit}>
                    <div className="input-group">
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Write a comment..."
                            value={commentText}
                            onChange={(e) => setCommentText(e.target.value)}
                            required
                            maxLength={1786}
                        />
                        <button className="btn btn-primary" type="submit">
                            Comment
                        </button>
                    </div>
                </form>

                {/* Display Comments */}
                <ul className="list-group" style={{ marginBottom: '15px' }}>
                    {comments.map((comment) => (
                        <li key={comment.id} className="list-group-item d-flex justify-content-between align-items-center">
                            <div>
                                <strong>{comment.username}</strong>: {comment.text}
                            </div>
                            <button
                                className="btn btn-sm btn-outline-success"
                                onClick={() => handleLikeComment(comment.id)}
                            >
                                👍 {comment.likes}
                            </button>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
};

export default Comments;
